import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useNotification } from 'app/providers'

import {
  useCreateSeriesMutation,
  useFetchSeriesTemplatesQuery,
  usePreviewSeriesMutation,
} from 'features/Series/api/series.api'
import { toSeriesApiError } from 'features/Series/api/series.transform'
import type { SeriesPreviewVisit, SeriesTemplate } from 'features/Series/api/series.types'
import { MAX_SERIES_VISITS } from 'features/Series/series.constants'

import type { GridEditPatch } from '../controls/SeriesGrid'
import type { SeriesFooter } from '../series.types'
import { samePeople, type VisitPeople } from '../seriesPeople'
import { hasNonVisitErrors } from '../seriesErrors'
import { seriesBookedNotice, seriesFailedNotice, seriesPreviewFailedNotice } from './seriesAnswer'
import {
  appendAddedRow,
  applyBandToAll,
  bandErrorsOf,
  cadenceKey,
  canSaveCreate,
  createBodyOf,
  createFooter,
  editCreateRow,
  headFromTemplate,
  intervalLabels,
  markCreateErrors,
  ownSteps,
  previewBodyOf,
  removeCreateRow,
  rowsFromPreview,
  type CreateBuilt,
  type CreateCadence,
  type CreateHead,
  type CreateRow,
  type CreateSeed,
} from './seriesCreate.model'

const DEFAULT_VISITS = 6
const DEFAULT_EVERY = 1

export type UseSeriesCreateArgs = {
  siteId: string
  resetKey: string
  active: boolean
  patientId: number | null
  band: VisitPeople | null
  categoryId: number | null
  providerName: string
  seed: CreateSeed
  onCategoryFromTemplate: (id: number) => void
}

export type CreateRowPatch = GridEditPatch

export type SeriesCreate = {
  ready: boolean
  head: CreateHead
  band: VisitPeople | null
  rows: CreateRow[]
  ownSteps: number[]
  bandErrors: string[]
  intervals: string[]
  templates: SeriesTemplate[]
  stale: boolean
  isPreviewing: boolean
  isSaving: boolean
  canAddVisit: boolean
  footer: SeriesFooter
  canSave: boolean
  setHead: (patch: Partial<CreateHead>) => void
  pickTemplate: (id: number | null) => void
  regenerate: () => void
  addVisit: () => void
  editRow: (key: string, patch: CreateRowPatch) => void
  applyBandToAll: () => void
  removeRow: (key: string) => void
  save: () => Promise<boolean>
}

// The band a failed save was sent with, and what the backend said about it.
type BandFailure = { band: VisitPeople; errors: string[] }

type Draft = {
  resetKey: string
  patientId: number | null
  // Bumped when the patient changes; an answer asked for another generation is dropped.
  generation: number
  head: CreateHead
  rows: CreateRow[]
  built: CreateBuilt | null
  previewing: boolean
  bandFailure: BandFailure | null
}

type Opening = { resetKey: string; generation: number }

const EMPTY_HEAD: CreateHead = {
  templateId: null,
  visits: DEFAULT_VISITS,
  every: DEFAULT_EVERY,
  unit: 'week',
  name: '',
  note: '',
  issueId: null,
}

const freshDraft = (resetKey: string, patientId: number | null): Draft => ({
  resetKey,
  patientId,
  generation: 0,
  head: EMPTY_HEAD,
  rows: [],
  built: null,
  previewing: false,
  bandFailure: null,
})

// A new opening starts blank; a new patient keeps the head but not the old patient's problem or rows.
const alignDraft = (d: Draft, resetKey: string, patientId: number | null): Draft => {
  if (d.resetKey !== resetKey) return freshDraft(resetKey, patientId)
  if (d.patientId === patientId) return d
  return {
    ...d,
    patientId,
    generation: d.generation + 1,
    head: { ...d.head, issueId: null },
    rows: [],
    built: null,
    previewing: false,
    bandFailure: null,
  }
}

const isOpening = (d: Draft, opening: Opening): boolean =>
  d.resetKey === opening.resetKey && d.generation === opening.generation

const isStale = (d: Pick<Draft, 'built' | 'head'>): boolean =>
  d.built !== null && cadenceKey(d.built) !== cadenceKey(d.head)

// Add and remove change the count only: a pending cadence change stays pending.
const resized = (d: Draft, rows: CreateRow[]): Draft => ({
  ...d,
  rows,
  head: isStale(d) ? d.head : { ...d.head, visits: rows.length },
  built: d.built ? { ...d.built, visits: rows.length } : d.built,
})

type PreviewAsk = {
  head: CreateHead
  from: { date: string; time: string }
  duration: number
  visits: number
  categoryId: number | null
}

export const useSeriesCreate = ({
  siteId,
  resetKey,
  active,
  patientId,
  band,
  categoryId,
  providerName,
  seed,
  onCategoryFromTemplate,
}: UseSeriesCreateArgs): SeriesCreate => {
  const [draftState, setDraft] = useState<Draft>(() => freshDraft(resetKey, patientId))
  const [saving, setSaving] = useState(false)
  const [previewSeries] = usePreviewSeriesMutation()
  const [createSeries] = useCreateSeriesMutation()
  const { setNotification } = useNotification()
  const { data: allTemplates } = useFetchSeriesTemplatesQuery(
    { siteId, includeRetired: false },
    { skip: !siteId },
  )
  // Latest preview wins; a new resetKey bumps it too.
  const requestRef = useRef(0)
  const builtForRef = useRef<string | null>(null)
  const seenResetRef = useRef(resetKey)
  const sendingRef = useRef(false)

  // A new opening or patient resets the draft in the same render.
  const draft = alignDraft(draftState, resetKey, patientId)
  if (draft !== draftState) setDraft(draft)
  const { head, generation, rows, bandFailure } = draft
  const opening = useMemo<Opening>(() => ({ resetKey, generation }), [resetKey, generation])
  // Previews ask with the band primary; a band change alone never re-previews.
  const previewDoctorId = band?.primaryId ?? null
  const cadence: CreateCadence = draft.built ?? head

  useEffect(() => {
    if (seenResetRef.current === resetKey) return
    seenResetRef.current = resetKey
    requestRef.current += 1
  }, [resetKey])

  const templates = useMemo(() => (allTemplates ?? []).filter((t) => t.active), [allTemplates])
  const ready = patientId !== null && band !== null

  // Resolves null when the answer is stale or failed.
  const runPreview = useCallback(
    async (ask: PreviewAsk): Promise<SeriesPreviewVisit[] | null> => {
      if (patientId === null || previewDoctorId === null) return null
      requestRef.current += 1
      const requestId = requestRef.current
      setDraft((d) => (isOpening(d, opening) ? { ...d, previewing: true } : d))
      const data = previewBodyOf({ patientId, doctorId: previewDoctorId, ...ask })
      try {
        const visits = await previewSeries({ siteId, data }).unwrap()
        if (requestId !== requestRef.current) return null
        setDraft((d) => ({ ...d, previewing: false }))
        return visits
      } catch (error) {
        if (requestId !== requestRef.current) return null
        setDraft((d) => ({ ...d, previewing: false }))
        setNotification(seriesPreviewFailedNotice(toSeriesApiError(error)))
        return null
      }
    },
    [patientId, previewDoctorId, opening, previewSeries, siteId, setNotification],
  )

  const build = useCallback(
    async (ask: PreviewAsk): Promise<void> => {
      const visits = await runPreview(ask)
      if (!visits) return
      const built: CreateBuilt = {
        visits: ask.head.visits,
        every: ask.head.every,
        unit: ask.head.unit,
      }
      // An answer for another opening or patient never lands on this draft.
      setDraft((d) => (isOpening(d, opening) ? { ...d, rows: rowsFromPreview(visits), built } : d))
    },
    [runPreview, opening],
  )

  const askFromFirst = useCallback(
    (nextHead: CreateHead, duration?: number): PreviewAsk => {
      const first = rows[0]
      return {
        head: nextHead,
        from: first ? { date: first.date, time: first.time } : { date: seed.date, time: seed.time },
        duration: duration ?? first?.duration ?? seed.duration,
        visits: nextHead.visits,
        categoryId,
      }
    },
    [rows, seed, categoryId],
  )

  // First active + ready of an opening (or of a new patient) builds the draft once.
  const buildKey = `${resetKey}#${generation}`
  useEffect(() => {
    if (!active || !ready || builtForRef.current === buildKey) return
    builtForRef.current = buildKey
    void build({
      head,
      from: { date: seed.date, time: seed.time },
      duration: seed.duration,
      visits: head.visits,
      categoryId,
    })
  }, [active, ready, buildKey, build, head, seed, categoryId])

  const setHead = useCallback((patch: Partial<CreateHead>) => {
    setDraft((d) => ({ ...d, head: { ...d.head, ...patch } }))
  }, [])

  const regenerate = useCallback(() => {
    void build(askFromFirst(head))
  }, [build, askFromFirst, head])

  const pickTemplate = useCallback(
    (id: number | null) => {
      if (id === null) return setHead({ templateId: null })
      const picked = templates.find((t) => t.id === id)
      if (!picked) return undefined
      const nextHead = headFromTemplate(head, picked)
      setDraft((d) => ({ ...d, head: nextHead }))
      if (picked.category) onCategoryFromTemplate(picked.category.id)
      const duration = picked.duration > 0 ? picked.duration : undefined
      void build({
        ...askFromFirst(nextHead, duration),
        categoryId: picked.category?.id ?? categoryId,
      })
      return undefined
    },
    [setHead, templates, head, onCategoryFromTemplate, build, askFromFirst, categoryId],
  )

  const addVisit = useCallback(async () => {
    const last = rows[rows.length - 1]
    if (!last || rows.length >= MAX_SERIES_VISITS) return
    // The next visit follows the cadence the rows were built with.
    const visits = await runPreview({
      head: { ...head, every: cadence.every, unit: cadence.unit },
      from: { date: last.date, time: last.time },
      duration: last.duration,
      visits: 2,
      categoryId,
    })
    if (!visits) return
    setDraft((d) => (isOpening(d, opening) ? resized(d, appendAddedRow(d.rows, visits)) : d))
  }, [rows, runPreview, head, cadence.every, cadence.unit, categoryId, opening])

  const editRow = useCallback((key: string, patch: CreateRowPatch) => {
    setDraft((d) => ({ ...d, rows: editCreateRow(d.rows, key, patch) }))
  }, [])

  const applyToAll = useCallback(() => {
    setDraft((d) => ({ ...d, rows: applyBandToAll(d.rows) }))
  }, [])

  const removeRow = useCallback((key: string) => {
    setDraft((d) => {
      const nextRows = removeCreateRow(d.rows, key)
      return nextRows === d.rows ? d : resized(d, nextRows)
    })
  }, [])

  const stale = isStale(draft)
  const canSave = canSaveCreate({
    head,
    rows,
    band,
    categoryId,
    isSaving: saving,
    isPreviewing: draft.previewing,
  })

  const save = useCallback(async (): Promise<boolean> => {
    if (sendingRef.current || !canSave || patientId === null || band === null) return false
    sendingRef.current = true
    setSaving(true)
    setDraft((d) => (d.bandFailure ? { ...d, bandFailure: null } : d))
    try {
      const data = createBodyOf({ patientId, band, categoryId, head, cadence, rows })
      const result = await createSeries({ siteId, data }).unwrap()
      setNotification(seriesBookedNotice(result.warnings))
      return true
    } catch (error) {
      const apiError = toSeriesApiError(error)
      // Errors land on the rows as they are now; a reopened dialog keeps its own draft.
      const { matched } = markCreateErrors(rows, apiError)
      const bandErrors = bandErrorsOf(apiError)
      if (matched || bandErrors.length) {
        setDraft((d) =>
          isOpening(d, opening)
            ? {
                ...d,
                rows: matched ? markCreateErrors(d.rows, apiError).rows : d.rows,
                bandFailure: bandErrors.length ? { band, errors: bandErrors } : d.bandFailure,
              }
            : d,
        )
      }
      if (!matched || hasNonVisitErrors(apiError.errors))
        setNotification(seriesFailedNotice(apiError))
      return false
    } finally {
      sendingRef.current = false
      setSaving(false)
    }
  }, [
    canSave,
    patientId,
    band,
    categoryId,
    head,
    cadence,
    rows,
    opening,
    createSeries,
    siteId,
    setNotification,
  ])

  const footer = createFooter({ head, rows, stale, providerName, canSave })

  return {
    ready,
    head,
    band,
    rows,
    ownSteps: ownSteps(rows),
    bandErrors: bandFailure && samePeople(bandFailure.band, band) ? bandFailure.errors : [],
    intervals: intervalLabels(rows, cadence),
    templates,
    stale,
    isPreviewing: draft.previewing,
    isSaving: saving,
    canAddVisit: rows.length < MAX_SERIES_VISITS,
    footer,
    canSave,
    setHead,
    pickTemplate,
    regenerate,
    addVisit: () => void addVisit(),
    editRow,
    applyBandToAll: applyToAll,
    removeRow,
    save,
  }
}
